import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocationSelection } from "../context/LocationSelectionContext";
import { cleanLocationName } from "../../../util/locationNameUtils";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "components/ui/select";
import { cn } from "lib/utils";
import { cityOf, townOf } from "../../../util/locationAddress";

interface LocationSelectDropdownProps {
  triggerClassName?: string;
  placeholder?: string;
}

/**
 * LocationSelectDropdown Component
 * ---------------------------------------------------------------
 * Dropdown for selecting a location using TanStack Query context.
 *
 * @remarks
 * Replaces FilteredSelect component that used Redux.
 * - Uses LocationSelectionContext for state management
 * - Automatically updates URL when selection changes
 * - Sorts locations by name and city
 */
export function LocationSelectDropdown({
  triggerClassName,
  placeholder,
}: LocationSelectDropdownProps) {
  const { t } = useTranslation();
  const { locations, selectedLocation, selectLocation } = useLocationSelection();

  const isDisabled = !locations || locations.length === 0;

  const selectOptions = useMemo(() => {
    if (!locations) return [];

    const sorted = [...locations].sort((a, b) => {
      const aName = cleanLocationName(a.name);
      const bName = cleanLocationName(b.name);
      const byName = aName.localeCompare(bName, "de", {
        sensitivity: "base",
        numeric: true,
      });
      if (byName !== 0) return byName;
      
      // Tie-breaker by city
      return cityOf(a).localeCompare(cityOf(b), "de", {
        sensitivity: "base",
        numeric: true,
      });
    });

    return sorted.map((location) => ({
      value: location.id,
      label: [cleanLocationName(location.name), townOf(location)]
        .filter(Boolean)
        .join(", "),
    }));
  }, [locations]);

  const handleLocationChange = useCallback(
    (value: string) => {
      const found = locations?.find((loc) => loc.id === value);
      if (found) {
        selectLocation(found);
      }
    },
    [locations, selectLocation]
  );

  return (
    <Select
      value={selectedLocation?.id ?? ""}
      onValueChange={handleLocationChange}
      disabled={isDisabled}
    >
      <SelectTrigger
        aria-label={t("book_location.location")}
        className={cn("h-auto items-start py-3", triggerClassName)}
        wrapText
      >
        <SelectValue placeholder={placeholder ?? t("book_location.location")} />
      </SelectTrigger>
      <SelectContent>
        {selectOptions.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            <span className="block max-w-full break-words whitespace-normal leading-snug">
              {opt.label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
